import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { useSavedListLists } from '@/entities/saved-creators-list';
import { AnimatedSuspense } from '@/shared/components/animated-suspense';
import { Button } from '@/shared/components/shadcn/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/shadcn/collapsible';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { FieldContent, FieldDescription, FieldLabel } from '@/shared/components/shadcn/field';
import { Input } from '@/shared/components/shadcn/input';
import { RadioGroup, RadioGroupItem } from '@/shared/components/shadcn/radio-group';
import type { DiscoverySaveToListResult } from './discovery-save-to-list-dialog.types';

export function DiscoverySaveToListDialog({
  close,
  initialTitle,
  isOpen,
  onSave,
}: {
  close: () => void;
  initialTitle: string;
  isOpen: boolean;
  onSave: (result: DiscoverySaveToListResult) => void;
}) {
  const [titleInput, setTitleInput] = useState(initialTitle);
  const [selectedList, setSelectedList] = useState<{
    listId: string;
    title: string;
  } | null>(null);

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save to List</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <h2 className="text-foreground text-sm font-medium">Create new list</h2>
            <Input
              placeholder="Type new list title"
              value={titleInput}
              onChange={(event) => setTitleInput(event.target.value)}
              onFocus={() => setSelectedList(null)}
            />
          </div>
          <AnimatedSuspense>
            <SavedLists selectedList={selectedList} setSelectedList={setSelectedList} />
          </AnimatedSuspense>
        </div>

        <DialogFooter>
          <Button onClick={close} variant="secondary">
            Cancel
          </Button>
          {selectedList ? (
            <Button
              onClick={() =>
                onSave({
                  action: 'save',
                  listId: selectedList.listId,
                  title: selectedList.title,
                })
              }
            >
              Add to selected list
            </Button>
          ) : (
            <Button
              onClick={() =>
                onSave({
                  action: 'create',
                  title: titleInput,
                })
              }
            >
              Create New List
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SavedLists({
  selectedList,
  setSelectedList,
}: {
  selectedList: {
    listId: string;
    title: string;
  } | null;
  setSelectedList: (list: { listId: string; title: string }) => void;
}) {
  const { data: lists } = useSavedListLists();

  return (
    lists.length > 0 && (
      <Collapsible>
        <CollapsibleTrigger className="flex w-full items-center justify-between">
          <h2 className="text-foreground text-sm font-medium">Add to existing list</h2>
          <ChevronDown className="size-4" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <RadioGroup
            value={selectedList?.listId}
            onValueChange={(id) => {
              const list = lists.find((item) => item.id === id);
              if (!list) return;
              setSelectedList({
                listId: id,
                title: list.title,
              });
            }}
            className="py-2"
          >
            {lists.map((list) => (
              <div key={list.id} className="flex items-center gap-4">
                <RadioGroupItem value={list.id} id={list.id} />
                <FieldContent>
                  <FieldLabel htmlFor={list.id}>{list.title}</FieldLabel>
                  <FieldDescription>{list.total_count} creators</FieldDescription>
                </FieldContent>
              </div>
            ))}
          </RadioGroup>
        </CollapsibleContent>
      </Collapsible>
    )
  );
}
